import React, { useState, useEffect } from 'react';
import GalleryGrid from './GalleryGrid';
import ImageDetail from './ImageDetail';
import useGallery, { GalleryImage } from '../../hooks/useGallery';
import SkewedCard from '../common/SkewedCard';

interface GalleryProps {
  adminMode?: boolean;
  onSelectImage?: (image: GalleryImage) => void;
  refreshTrigger?: number;
}

const Gallery: React.FC<GalleryProps> = ({
  adminMode = false,
  onSelectImage,
  refreshTrigger = 0
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [localRefresh, setLocalRefresh] = useState(0);

  // Use the combined refresh trigger to refresh the gallery
  useEffect(() => {
    // When refreshTrigger changes, update our local refresh state to cause a re-fetch
    setLocalRefresh(prev => prev + 1);
  }, [refreshTrigger]);

  const { images, loading, error } = useGallery({
    refreshTrigger: localRefresh
  });

  const handleImageClick = (image: GalleryImage) => {
    if (adminMode && onSelectImage) {
      onSelectImage(image);
    } else {
      const index = images.findIndex(img => img.id === image.id);
      setSelectedImageIndex(index);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <SkewedCard borderColor="red" backgroundColor="#fee" padding="p-4" className="mb-4">
        <p className="text-red-700">{error}</p>
      </SkewedCard>
    );
  }

  return (
    <div>
      <GalleryGrid
        images={images}
        onImageClick={handleImageClick}
      />

      {selectedImageIndex !== null && (
        <ImageDetail
          images={images}
          initialIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
        />
      )}

      {images.length === 0 && !loading && (
        <SkewedCard className="text-center" padding="p-12" backgroundColor="#f9fafb">
          <p className="text-gray-500 text-lg">No images found in the gallery.</p>
        </SkewedCard>
      )}
    </div>
  );
};

export default Gallery;